import Vue from 'vue'
import Api from '~/store/idonia-api/api.js'
import FILE_STATES from './file-states.js'
import { api } from 'dicomweb-client'
import { v4 as uuidv4 } from 'uuid'
import store from '~/store/store'

const MAX_UPLOADING_FILES = 10
const MAX_TRACKING_FILES = 100
export default new Vue({
  data () {
    return {
      files: {},
      filesByState: {
        [FILE_STATES.pending]: {},
        [FILE_STATES.uploading]: {},
        [FILE_STATES.completed]: {},
        [FILE_STATES.error]: {},
        [FILE_STATES.canceled]: {}
      },
      error402: false,
      folders: false
    }
  },
  computed: {
    totalFiles () {
      return Object.keys(this.files).length
    },
    isSimplifiedUploader () {
      return this.totalFiles > MAX_TRACKING_FILES
    },
    disconnected () {
      return store.state.session.networkDisconnected
    }
  },
  watch: {
    totalFiles (v, o) {
      if (o === 0 && v > 0) {
        this.$emit('files-added')
      }
    },
    disconnected (disconnected) {
      if (disconnected) {
        Object.keys(this.files).forEach(key => {
          if (this.files[key].state !== FILE_STATES.completed && this.files[key].state !== FILE_STATES.canceled) {
            this.updateFileState(key, FILE_STATES.error)
            this.updateFileRetries(6, key)
          }
        })
      } else {
        Object.keys(this.files).forEach(key => {
          if (this.files[key].state === FILE_STATES.error) {
            this.updateFileState(key, FILE_STATES.pending)
            this.updateFileRetries(0, key)
          }
        })
      }
    }
  },
  methods: {
    processPending () {
      const uploadingKeys = Object.keys(this.filesByState[FILE_STATES.uploading])
      const pendingKeys = Object.keys(this.filesByState[FILE_STATES.pending])
      if (pendingKeys.length > 0 && uploadingKeys.length < MAX_UPLOADING_FILES) {
        this.uploadFile(pendingKeys[0]).catch(e => {
          console.debug(e)
        })
      }
    },
    addFile (file) {
      return new Promise((resolve, reject) => {
        const statefulFile = Object.assign({}, {
          state: FILE_STATES.pending,
          progress: 0,
          retries: 0,
          request: undefined,
          resolve,
          reject
        }, file)
        const fileUID = uuidv4()
        this.$set(this.files, fileUID, statefulFile)
        this.$set(this.filesByState[FILE_STATES.pending], fileUID, true)
        this.updateFileState(fileUID, FILE_STATES.pending)
      })
    },
    updateFileState (key, state) {
      if (state[Symbol.toStringTag] === 'FileState') {
        const oldState = this.files[key].state

        this.files[key].state = state
        this.$set(this.filesByState[state], key, true)
        if (oldState !== state) {
          if (this.filesByState[oldState] && this.filesByState[oldState][key]) {
            this.$delete(this.filesByState[oldState], key)
          }
        }
        this.processPending()
      } else {
        console.error('File state must be FileState instance, instead got: ', state[Symbol.toStringTag])
      }
    },
    updateFileProgress (key, progress) {
      if (!this.files[key]) return
      this.files[key].progress = progress > 100 ? 100 : progress
    },
    updateFileRequest (key, request) {
      if (!this.files[key]) return
      this.files[key].request = request
    },
    abortUpload (key) {
      const data = this.files[key]
      if (data.request !== undefined) {
        let abortPromise = null
        if (data.abort) {
          abortPromise = Promise.resolve(data.abort())
        } else {
          abortPromise = data.request.then(xhr => {
            xhr.abort()
          })
        }
        abortPromise.then(() => {
          this.updateFileState(key, FILE_STATES.canceled)
          this.updateFileProgress(key, 0)
        })
      } else {
        console.warn('Can\'t abort upload, request promise doesn\'t exists yet.')
      }
    },
    uploadFile (key) {
      const data = this.files[key]
      return new Promise(async (resolve, reject) => {
        const post = () => {
          const formData = new FormData()
          formData.append('files', data.file)

          for (let name in data.params) {
            formData.append(name, data.params[name])
          }

          this.updateFileState(key, FILE_STATES.uploading)
          let onProgress
          if (!this.isSimplifiedUploader) {
            onProgress = (onProgressEvent) => {
              const percent = (onProgressEvent.loaded / onProgressEvent.total) * 100
              this.updateFileProgress(key, percent)
            }
          }
          const requestPromise = Api.post(data.endpoint,
            formData,
            resolve,
            reject,
            false,
            {'Content-Type': 'multipart/form-data'},
            onProgress
          ).catch((e) => {
            console.debug(e)
          })
          this.updateFileRequest(key, requestPromise)
        }

        if (data.endpoint === 'document' || data.endpoint.startsWith('containers')) {
          this.updateFileState(key, FILE_STATES.uploading)

          Api.getV2('document/policy')
            .then(response => {
              if (response.status !== 201) {
                throw new Error(response)
              }
              const policy = response.body['policy']
              const signature = response.body['signature']
              const uuid = response.body['uuid']
              const url = response.body['url']
              const clientEmail = response.body['client_email']

              const urlParts = url.split('/')

              const formData = new FormData()
              formData.append('GoogleAccessId', clientEmail)
              formData.append('key', 'in/' + uuid)
              formData.append('bucket', urlParts[urlParts.length - 1])
              formData.append('policy', policy)
              formData.append('signature', signature)
              formData.append('file', data.file)

              Vue.http.post(url, formData, {
                progress: (onProgressEvent) => {
                  const percent = (onProgressEvent.loaded / onProgressEvent.total) * 100
                  this.updateFileProgress(key, percent)
                },
                before: (rq) => {
                  this.updateFileRequest(key, Promise.resolve(rq))
                }
              })
              .then(() => {
                const formData = new FormData()
                formData.append('uuid', uuid)
                // TODO file_name gets duplicated
                formData.append('file_name', data.file.name)
                for (let name in data.params) {
                  formData.append(name, data.params[name])
                }

                const requestPromise = Api.post(data.endpoint,
                  formData,
                  resolve,
                  reject
                )
                this.updateFileRequest(key, requestPromise)
              })
              .catch(() => {
                if (!this.files[key].state.equals(FILE_STATES.canceled)) {
                  this.updateFileState(key, FILE_STATES.error)
                }
              })
            })
            .catch(error => {
              if (error.status === 402 && !this.files[key].state.equals(FILE_STATES.canceled)) {
                this.updateFileState(key, FILE_STATES.error)
                reject(error)
                this.error402 = true
                return
              }
              post()
            })
        } else if (data.endpoint.startsWith('vault')) {
          this.updateFileState(key, FILE_STATES.uploading)

          try {
            const remote = store.getters['session/getSelectedRemote']
            const payload = {
              remote: remote.remote_id,
              scope: remote.scope,
              endpoint: data.endpoint.split('/').pop()
            }
            const remoteData = await store.dispatch('session/getRemoteAuth', payload)
            const request = new XMLHttpRequest()
            const isDocument = payload.endpoint === 'document'
            request.responseType = isDocument ? 'json' : request.responseType
            const authHeader = `Bearer ${remoteData.token}`
            this.files[key].abort = () => { request.abort() }

            const progressCallback = evt => {
              if (!evt.lengthComputable) {
                return
              }
              const percent = Math.round((100 * evt.loaded) / evt.total)
              this.updateFileProgress(key, percent)
            }
            request.upload.addEventListener('progress', progressCallback)

            request.onload = () => {
              if (request.status >= 200 && request.status < 300) {
                if (typeof data.callback === 'function') {
                  data.callback(request.response).then(() => {
                    resolve(request.response)
                  }).catch((e) => {
                    console.debug(e)
                    if (!this.files[key].state.equals(FILE_STATES.canceled)) {
                      this.updateFileState(key, FILE_STATES.error)
                    }
                  })
                } else {
                  resolve(request.response)
                }
              } else {
                reject(request.statusText)
              }
            }
            request.onerror = () => { reject(request.statusText) }
            if (isDocument) {
              const formData = new FormData()
              formData.append('file', data.file)
              request.open('POST', remoteData.url)
              request.setRequestHeader('Authorization', authHeader)
              request.send(formData)
              this.updateFileRequest(key, request)
            } else {
              const loadedFile = await this.readFile(data.file)
              const client = new api.DICOMwebClient({
                url: remoteData.url,
                headers: {
                  Authorization: authHeader
                }
              })
              const requestPromise = client.storeInstances({ datasets: [loadedFile.content], request })
                .catch(err => {
                  console.error(err)
                  if (!this.files[key].state.equals(FILE_STATES.canceled)) {
                    this.updateFileState(key, FILE_STATES.error)
                  }
                })

              this.updateFileRequest(key, requestPromise)
            }
          } catch (error) {
            console.error(error)
            if (!this.files[key].state.equals(FILE_STATES.canceled)) {
              this.updateFileState(key, FILE_STATES.error)
            }
          }
        } else {
          post()
        }
      }).then(response => {
        data.resolve()
        this.updateFileState(key, FILE_STATES.completed)
        return response
      }, error => {
        data.reject(data)
        if (!data.state.equals(FILE_STATES.canceled)) {
          this.updateFileState(key, FILE_STATES.error)
        }
        throw error
      })
    },
    clearFiles () {
      this.files = {}
      this.filesByState = {
        [FILE_STATES.pending]: {},
        [FILE_STATES.uploading]: {},
        [FILE_STATES.completed]: {},
        [FILE_STATES.error]: {},
        [FILE_STATES.canceled]: {}
      }
    },
    updateFileRetries (retries, uid) {
      this.files[uid].retries = retries
    },
    readFile (file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = () => {
          resolve({
            name: file.name,
            size: file.size,
            type: file.type,
            content: reader.result
          })
        }
        reader.onerror = error => reject(error)
        reader.readAsArrayBuffer(file)
      })
    }
  }
})



// WEBPACK FOOTER //
// ./src/plugins/uploader/upload-state.js